import React from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import { getSelectedPlanInfo } from '../../utils/getSelectedPlanInfo';
import {
  resolveProtectionCardVariant,
  type ProtectionCardVariant,
} from '../../utils/resolveProtectionCardVariant';

import type { ProtectionCardFactoryProps, ProtectionCardType } from './protectionCard.types';
import ProtectionCardSkeleton from './ProtectionCardSkeleton';
import ActiveTrialVariant, { TRIAL_ACTIVE_PROTECTION_CARD_TYPE } from './variants/ActiveTrialVariant';
import PendingInvoiceVariant, { PENDING_INVOICE_PROTECTION_CARD_TYPE } from './variants/PendingInvoiceVariant';
import ProPlanVariant, { AUTOMATIC_PROTECTION_AVAILABLE_PROTECTION_CARD_TYPE } from './variants/ProPlanVariant';
import TrialExpiredVariant, { TRIAL_EXPIRED_PROTECTION_CARD_TYPE } from './variants/TrialExpiredVariant';
import UpgradeCycleVariant, { UPGRADE_CYCLE_PROTECTION_CARD_TYPE } from './variants/UpgradeCycleVariant';

const mapForceRenderingTypeToVariant = (
  forceRenderingType?: ProtectionCardType,
): ProtectionCardVariant | null => {
  if (!forceRenderingType) {
    return null;
  }

  switch (forceRenderingType) {
    case PENDING_INVOICE_PROTECTION_CARD_TYPE:
      return 'pendingInvoice';
    case TRIAL_EXPIRED_PROTECTION_CARD_TYPE:
      return 'trialExpired';
    case TRIAL_ACTIVE_PROTECTION_CARD_TYPE:
      return 'trialActive';
    case UPGRADE_CYCLE_PROTECTION_CARD_TYPE:
      return 'upgradeCycle';
    case AUTOMATIC_PROTECTION_AVAILABLE_PROTECTION_CARD_TYPE:
      return 'proPlan';
    default:
      return null;
  }
};

const ProtectionCardFactory = ({
  testId = 'protection-card',
  isLoading = false,
  selectedPlan = null,
  isUpgradeCycleModalEnabled = false,
  isAAClient = false,
  hideButtons = false,
  hideActivateTrial = false,
  onActivateProtectionPro,
  onActivateTrial,
  onUpgradeCycleClick,
  onPendingInvoiceClick,
  isTurbo = false,
  enableTrial = true,
  forceRenderingType,
  upgradeCycleDiscountPercent = 0,
  upgradeCyclePricingLoading = false,
}: ProtectionCardFactoryProps) => {
  if (isLoading) {
    return <ProtectionCardSkeleton />;
  }

  const planInfo = getSelectedPlanInfo(selectedPlan);
  const variant = mapForceRenderingTypeToVariant(forceRenderingType)
    ?? resolveProtectionCardVariant(selectedPlan, { isUpgradeCycleModalEnabled });

  switch (variant) {
    case 'none':
      return null;
    case 'pendingInvoice':
      return (
        <PendingInvoiceVariant
          testId={testId}
          onPendingInvoiceClick={onPendingInvoiceClick}
        />
      );
    case 'trialExpired':
      return (
        <TrialExpiredVariant
          testId={testId}
          hideButtons={hideButtons}
          onActivateProtectionPro={onActivateProtectionPro}
        />
      );
    case 'trialActive':
      return (
        <ActiveTrialVariant
          testId={testId}
          hideButtons={hideButtons}
          onActivateProtectionPro={onActivateProtectionPro}
        />
      );
    case 'upgradeCycle':
      return (
        <UpgradeCycleVariant
          testId={testId}
          discountPercent={upgradeCycleDiscountPercent}
          fromAnnualHosting={planInfo.isAnnualHostingCycle}
          isButtonLoading={upgradeCyclePricingLoading}
          onButtonClick={onUpgradeCycleClick}
        />
      );
    case 'proPlan':
    default:
      return (
        <ProPlanVariant
          testId={testId}
          hideButtons={hideButtons}
          hideActivateTrial={isTurbo || hideActivateTrial}
          isAAClient={isAAClient}
          onActivateProtectionPro={onActivateProtectionPro}
          onActivateTrial={onActivateTrial}
          enableTrial={enableTrial}
        />
      );
  }
};

export default withErrorBoundary(ProtectionCardFactory, 'ProtectionCardFactory');
